import {
  AlignmentType,
  Document,
  Footer,
  Header,
  LevelFormat,
  Packer,
  PageNumber,
  Paragraph,
  TextRun,
  UnderlineType,
  convertInchesToTwip,
} from "docx";
import { processAnswer, processDependencies } from "./TextProcessor";
import { saveAs } from "file-saver";
import { stylesPerCompany } from "./stylesPerCompany/stylesPerCompany";

export const processVariablesToString = (text: string) => {
  if (text.includes("{")) {
    return text
      .replaceAll("{", "#{")
      .replaceAll("}", "}#")
      .split("#")
      .map((t: string) => {
        if (t.includes("{")) {
          let isBold = t.includes("^");
          const te = t.replaceAll("^", "");
          try {
            const companyData = JSON.parse(
              window.localStorage.getItem("companyData") || "{}"
            );

            const currentAnswers = JSON.parse(
              window.localStorage.getItem("generator-form-answers") || "{}"
            );

            const answers = { companyData, ...currentAnswers };

            const str: any = te.slice(0, -1).substring(1);

            if (str.includes(".")) {
              const split = str.split(".");
              if (answers[split[0]] && answers[split[0]][split[1]])
                if (isBold)
                  return `[${processAnswer(answers[split[0]][split[1]])}]`;
              return processAnswer(answers[split[0]][split[1]]);
            }

            if (answers && answers[str]) {
              if (isBold) return `[${processAnswer(answers[str])}]`;

              return processAnswer(answers[str]);
            }
            return "___________________";
          } catch {
            return t;
          }
        }
        return t;
      })
      .join("");
  }
  return text;
};

type ParagraphType = {
  id: string;
  text: string;
  type: string;
};

// esta funcion es necesaria por que cuando tenes un SameParagraph
// tenes que volver para atras y agregarlo al elemento anterior
// es una funcion dificil de entender, pero funciona
export const joinSameParagraphs = (
  paragraphs: ParagraphType[]
): ParagraphType[] => {
  if (!paragraphs) return [];
  return paragraphs.reduce((acc: any[], paragraph: any, index: number) => {
    if (paragraph.type === "sameParagraph" && acc[acc.length - 1]?.text) {
      acc[acc.length - 1].text += paragraph.text;
    } else {
      acc.push(paragraph);
    }
    return acc;
  }, []);
};

export const ProcessWordData = (documentData: any) => {
  const processedTextArray = documentData?.document
    ?.filter((paragraph: any, index: number) => {
      let shouldRender = true;
      if (paragraph.dependencies) {
        shouldRender = processDependencies(paragraph.dependencies);
      }
      return shouldRender;
    })
    .map((paragraph: any) => {
      return {
        type: paragraph.type,
        text: processVariablesToString(paragraph.text),
      };
    });
  return processedTextArray;
};

const ConvertDataToWordElements = (documentData: any, companyId?: any) => {
  let styles = {
    font: "Arial",
    titleSize: 21,
    textSize: 21,
    lineSpacing: 470,
    marginRight: 780,
    marginLeft: 780,
    beforeParagraph: 300,
    footer: new Footer({
      children: [
        new Paragraph({
          spacing: {
            before: 300,
            after: 100,
          },
          alignment: AlignmentType.CENTER,
          children: [],
        }),
      ],
    }),
    header: new Header({
      children: [
        new Paragraph({
          spacing: {
            before: 300,
            after: 100,
          },
          alignment: AlignmentType.RIGHT,
          children: [],
        }),
      ],
    }),
  };

  if (companyId) {
    const companyStyles = stylesPerCompany.find((style: any) =>
      style.companyId.includes(String(companyId))
    );

    if (companyStyles) {
      styles = companyStyles;
    }
  }

  if (
    localStorage.getItem("removeLogo") &&
    String(localStorage.getItem("removeLogo")) === "true"
  ) {
    styles.header = new Header({
      children: [
        new Paragraph({
          spacing: {
            before: 300,
            after: 100,
          },
          alignment: AlignmentType.RIGHT,
          children: [],
        }),
      ],
    });
    styles.footer = new Footer({
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [
            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  children: [],
                }),
              ],
            }),
            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  children: [],
                }),
              ],
            }),
            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  children: [PageNumber.CURRENT],
                }),
              ],
            }),
          ],
        }),
      ],
    });
  }

  const processedData = ProcessWordData(documentData);
  const joinedParagraphs = joinSameParagraphs(processedData);
  const paragraphs = joinedParagraphs.map((paragraph: any, index: number) => {
    const textArray = paragraph.text
      .replaceAll("[", "#[")
      .replaceAll("]", "]#")
      .replaceAll("<", "#<")
      .replaceAll(">", ">#")
      .split("#")
      .map((t: string) => {
        if (t.includes("[")) {
          return new TextRun({
            text: t.replaceAll("[", "").replaceAll("]", ""),
            size: styles.textSize,
            bold: true,
            font: styles.font,
          });
        }
        if (t.includes("<")) {
          return new TextRun({
            text: t.replaceAll("<", "").replaceAll(">", ""),
            size: styles.textSize,
            bold: false,
            font: styles.font,
            underline: {
              type: UnderlineType.SINGLE,
              color: "000000",
            },
          });
        }
        if (paragraph.type === "title")
          return new TextRun({
            text: t,
            bold: true,
            size: styles.titleSize,
            font: styles.font,
            underline: {
              type: UnderlineType.SINGLE,
              color: "000000",
            },
          });
        if (paragraph.type === "centeredText")
          return new TextRun({
            text: t,
            size: styles.textSize,
            font: styles.font,
          });
        if (paragraph.type === "subtitle")
          return new TextRun({
            text: t,
            size: styles.textSize,
            bold: true,
            font: styles.font,
          });
        if (paragraph.type === "bullet")
          return new TextRun({
            text: t,
            size: styles.textSize,
            // bullet: true,
            font: styles.font,
          });
        if (paragraph.type === "rightAlignedText")
          return new TextRun({
            text: t,
            size: styles.textSize,
            font: styles.font,
          });
        if (paragraph.type === "newParagraph")
          return new TextRun({
            text: t,
            size: styles.textSize,
            font: styles.font,
          });
        return new TextRun({
          text: t,
          size: styles.textSize,
          font: styles.font,
        });
      });

    const isBullet = paragraph.type === "bullet";
    const isNotNewParagraph = paragraph.type === "text";
    const isSignature = paragraph.type === "signature";
    let align =
      paragraph.type === "title" || paragraph.type === "centeredText"
        ? AlignmentType.CENTER
        : AlignmentType.JUSTIFIED;

    if (paragraph.type === "rightAlignedText") {
      align = AlignmentType.RIGHT;
    }

    if (isBullet) {
      return new Paragraph({
        children: [...textArray],
        spacing: {
          before: styles.beforeParagraph,
          line: styles.lineSpacing,
        },

        alignment: align,
        numbering: {
          reference: "my-bullet-points",
          level: 0,
        },
      });
    }

    const getSpacingBefore = () => {
      if (isSignature) return 1000;
      if (isNotNewParagraph) return 0;
      return styles.beforeParagraph;
    };

    return new Paragraph({
      children: [...textArray],
      spacing: {
        before: getSpacingBefore(),
        line: styles.lineSpacing,
      },
      alignment: align,
    });
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 0,
              right: styles.marginRight,
              bottom: 0,
              left: styles.marginLeft,
            },
          },
        },
        headers: {
          default: styles.header,
        },
        footers: {
          default: styles.footer,
        },
        children: [...paragraphs],
      },
    ],
    numbering: {
      config: [
        {
          reference: "my-bullet-points",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "\u2022",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
          ],
        },
      ],
    },
  });

  return doc;
};

export const generateAndDownloadWord = async (
  documentData: any,
  companyId?: string,
  fileName?: string
) => {
  const doc = ConvertDataToWordElements(documentData, companyId);
  const blob = await Packer.toBlob(doc);
  const file = new File([blob], "test.docx", {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  let newDocName = "PRUEBA";
  if (fileName) newDocName = fileName;
  saveAs(blob, `${newDocName}.docx`);
  return file;
};
